import { useEffect, useState } from "react";
import { Product, Shop } from "../../types/Shop";
import DB_PATH from "../DB_PATH";
import { firestore } from "../firebase";

// Author-scoped shop. Mirrors the community shop (Community/{id}/Shop) but
// lives under Author/{authorId}/Shop so each coach can have their own
// offerings. Kept separate from the community shop database layer so those
// live flows are untouched.
const authorShopDoc = (authorId: string) =>
  firestore
    .collection(DB_PATH.AUTHOR)
    .doc(authorId)
    .collection("Shop")
    .doc("shop");

// Flip the app-facing gate so the shop appears on the author's profile.
export const enableAuthorShop = async (authorId: string): Promise<void> => {
  if (!authorId) return;
  await firestore
    .collection(DB_PATH.AUTHOR)
    .doc(authorId)
    .set({ shopEnabled: true }, { merge: true });
};

// Live products for an author's shop, ordered by index.
export const useAuthorProducts = ({ authorId }: { authorId?: string }) => {
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    if (!authorId) {
      setProducts([]);
      return;
    }

    const ref = authorShopDoc(authorId).collection("products");
    const unsubscribe = ref.onSnapshot(
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          ...(doc.data() as Product),
          id: doc.id,
        }));
        data.sort((a, b) => (a.index || 0) - (b.index || 0));
        setProducts(data);
      },
      (error) => {
        console.error("Error fetching author products", error);
        setProducts([]);
      }
    );

    return () => unsubscribe();
  }, [authorId]);

  return products;
};

// Live shop metadata for an author.
export const useAuthorShop = ({ authorId }: { authorId?: string }) => {
  const [shop, setShop] = useState<Shop | null>(null);

  useEffect(() => {
    if (!authorId) {
      setShop(null);
      return;
    }

    const unsubscribe = authorShopDoc(authorId).onSnapshot(
      (snapshot) =>
        setShop(
          snapshot.exists ? ({ id: authorId, ...snapshot.data() } as Shop) : null
        ),
      (error) => {
        console.error("Error fetching author shop", error);
        setShop(null);
      }
    );

    return () => unsubscribe();
  }, [authorId]);

  return shop;
};

export const upsertAuthorShop = async ({
  authorId,
  shop,
}: {
  authorId: string;
  shop: Partial<Shop>;
}): Promise<void> => {
  if (!authorId) throw new Error("authorId is required to save the shop");
  const { id, ...payload } = shop as any;
  await authorShopDoc(authorId).set(payload, { merge: true });
  await enableAuthorShop(authorId);
};

export const createAuthorProduct = async ({
  authorId,
  product,
}: {
  authorId: string;
  product: Product;
}): Promise<string> => {
  if (!authorId) throw new Error("authorId is required to create a product");
  const productsRef = authorShopDoc(authorId).collection("products");
  const productRef = product.id ? productsRef.doc(product.id) : productsRef.doc();
  const id = product.id || productRef.id;
  await productRef.set({ ...product, id });
  await enableAuthorShop(authorId);
  return id;
};

export const updateAuthorProduct = async ({
  authorId,
  product,
}: {
  authorId: string;
  product: Product;
}): Promise<void> => {
  if (!authorId || !product.id) {
    throw new Error("authorId and product.id are required to update");
  }
  await authorShopDoc(authorId)
    .collection("products")
    .doc(product.id)
    .set(product, { merge: true });
  await enableAuthorShop(authorId);
};

export const deleteAuthorProduct = async ({
  authorId,
  productId,
}: {
  authorId: string;
  productId: string;
}): Promise<void> => {
  if (!authorId || !productId) return;
  await authorShopDoc(authorId).collection("products").doc(productId).delete();
};
